/**
 * Pending Approvals Component
 * Displays documents pending approval for the current user
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Loader,
  AlertCircle,
  Clock,
  User,
  Check,
  X,
  SkipForward,
  MessageSquare,
  Eye,
  ChevronRight,
  AlertTriangle,
  Info
} from 'lucide-react';
import { Card, CardHeader, CardBody, CardFooter } from '../../common/Card';
import { Button } from '../../common/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { workflowService, documentService } from '../../../services/api/documentGeneratorService';
import {
  PendingApproval,
  GeneratedDocument,
  ApprovalActionInput,
  DOCUMENT_CATEGORY_LABELS
} from '../../../types/documentGenerator.types';

interface PendingApprovalsProps {
  onViewDocument?: (document: GeneratedDocument) => void;
}

export const PendingApprovals: React.FC<PendingApprovalsProps> = ({
  onViewDocument
}) => {
  const { token } = useAuth();
  const [approvals, setApprovals] = useState<PendingApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [showCommentModal, setShowCommentModal] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'skip' | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const fetchApprovals = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const result = await workflowService.getPendingApprovals(token, pagination.page, pagination.limit);
      setApprovals(result.data);
      setPagination(prev => ({
        ...prev,
        total: result.pagination.total,
        totalPages: result.pagination.totalPages
      }));
    } catch (err: any) {
      setError(err.message || 'Failed to fetch pending approvals');
    } finally {
      setLoading(false);
    }
  }, [token, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const handleApprovalAction = async (documentId: string, action: 'approve' | 'reject' | 'skip', comments?: string) => {
    if (!token) return;

    setProcessingId(documentId);
    try {
      const input: ApprovalActionInput = {
        action,
        comments
      };
      await documentService.processApprovalAction(token, documentId, input);
      setShowCommentModal(null);
      setComment('');
      setActionType(null);
      fetchApprovals();
    } catch (err: any) {
      setError(err.message || `Failed to ${action} document`);
    } finally {
      setProcessingId(null);
    }
  };

  const openCommentModal = (documentId: string, action: 'approve' | 'reject' | 'skip') => {
    setShowCommentModal(documentId);
    setActionType(action);
    setComment('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionColor = (action: 'approve' | 'reject' | 'skip') => {
    switch (action) {
      case 'approve':
        return 'bg-green-600 hover:bg-green-700';
      case 'reject':
        return 'bg-red-600 hover:bg-red-700';
      case 'skip':
        return 'bg-amber-600 hover:bg-amber-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-text-primary">Pending Approvals</h2>
          <p className="text-text-secondary mt-1">
            {pagination.total} document{pagination.total !== 1 ? 's' : ''} awaiting your action
          </p>
        </div>
        <Button variant="secondary" onClick={fetchApprovals} className="gap-2">
          <Loader className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : approvals.length === 0 ? (
        <Card className="text-center py-12">
          <Check className="w-12 h-12 mx-auto text-green-500 mb-4" />
          <h3 className="text-lg font-medium text-text-primary">All caught up!</h3>
          <p className="text-text-secondary mt-1">
            You have no pending approvals at this time
          </p>
        </Card>
      ) : (
        /* Approval Cards */
        <div className="space-y-4">
          {approvals.map(approval => (
            <Card key={approval.document._id} hover={false}>
              <div className="flex items-start justify-between gap-6">
                {/* Document Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-primary-50 dark:bg-primary-900/20">
                      <FileText className="w-6 h-6 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-text-primary truncate">
                          {approval.document.title}
                        </h3>
                        <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-text-secondary rounded">
                          {DOCUMENT_CATEGORY_LABELS[approval.document.category]}
                        </span>
                      </div>
                      <p className="text-sm text-text-tertiary">{approval.document.documentNumber}</p>

                      {/* Step Info */}
                      <div className="flex items-center gap-4 mt-3 text-sm text-text-secondary">
                        <div className="flex items-center gap-1">
                          <ChevronRight className="w-4 h-4" />
                          <span>Step {approval.step.stepNumber}: {approval.step.name}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          <span>
                            Created by {approval.document.createdBy.firstName} {approval.document.createdBy.lastName}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{formatDate(approval.document.createdAt)}</span>
                        </div>
                      </div>

                      {/* Reason if cannot approve */}
                      {!approval.canApprove && approval.reason && (
                        <div className="flex items-center gap-2 mt-3 p-2 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 rounded-lg text-sm">
                          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                          <span>{approval.reason}</span>
                        </div>
                      )}

                      {/* Optional step indicator */}
                      {approval.step.isOptional && (
                        <div className="flex items-center gap-2 mt-2 text-sm text-text-tertiary">
                          <Info className="w-4 h-4" />
                          <span>This is an optional approval step</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onViewDocument?.(approval.document)}
                    className="gap-1"
                  >
                    <Eye className="w-4 h-4" />
                    View
                  </Button>

                  {approval.canApprove && (
                    <>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => openCommentModal(approval.document._id, 'approve')}
                        disabled={processingId === approval.document._id}
                        className="gap-1 bg-green-600 hover:bg-green-700"
                      >
                        {processingId === approval.document._id ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                        Approve
                      </Button>

                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => openCommentModal(approval.document._id, 'reject')}
                        disabled={processingId === approval.document._id}
                        className="gap-1 bg-red-600 hover:bg-red-700"
                      >
                        <X className="w-4 h-4" />
                        Reject
                      </Button>

                      {approval.step.isOptional && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleApprovalAction(approval.document._id, 'skip')}
                          disabled={processingId === approval.document._id}
                          className="gap-1"
                        >
                          <SkipForward className="w-4 h-4" />
                          Skip
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={pagination.page === 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            Previous
          </Button>
          <span className="text-sm text-text-secondary px-4">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={pagination.page === pagination.totalPages}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            Next
          </Button>
        </div>
      )}

      {/* Comment Modal */}
      {showCommentModal && actionType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <Card className="w-full max-w-md">
            <CardHeader
              title={`${actionType === 'approve' ? 'Approve' : actionType === 'reject' ? 'Reject' : 'Skip'} Document`}
              subtitle="Add a comment for your action (optional)"
            />
            <CardBody>
              <div className="space-y-4">
                <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
                  <MessageSquare className="w-5 h-5 text-text-tertiary" />
                  <span className="text-sm text-text-secondary">
                    Your comment will be visible to the document creator
                  </span>
                </div>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-surface-base text-text-primary px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  placeholder={`Add a comment for this ${actionType}...`}
                />
              </div>
            </CardBody>
            <CardFooter>
              <Button variant="secondary" onClick={() => setShowCommentModal(null)}>
                Cancel
              </Button>
              <Button
                className={getActionColor(actionType)}
                onClick={() => handleApprovalAction(showCommentModal, actionType, comment)}
                disabled={processingId === showCommentModal}
              >
                {processingId === showCommentModal ? (
                  <Loader className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                {actionType === 'approve' ? 'Approve' : actionType === 'reject' ? 'Reject' : 'Skip'}
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PendingApprovals;
